import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';

const MenuSelector = ({ menus, activeMenuId, onSelect, onAdd, onDelete, onRename, readOnly = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [tempName, setTempName] = useState('');
  const containerRef = useRef(null);

  const activeMenu = menus.find(m => m.id === activeMenuId) || menus[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsRenaming(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRenameSubmit = (e) => {
    e.preventDefault();
    if (tempName.trim()) {
      onRename(activeMenuId, tempName.trim());
      setIsRenaming(false);
    }
  };

  const startRenaming = (e) => {
    e.stopPropagation();
    setTempName(activeMenu.title);
    setIsRenaming(true);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors text-sm font-medium min-w-[160px] justify-between group border border-transparent hover:border-slate-300"
      >
        <span className="truncate max-w-[120px]">{activeMenu.title}</span>
        <SafeIcon icon={FiIcons.FiChevronDown} className={`text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden z-50 origin-top-left"
          >
            <div className="p-2 border-b border-slate-50 bg-slate-50/50">
              <span className="text-[10px] uppercase font-bold text-slate-400 px-2">Switch Sitemap</span>
            </div>
            
            <div className="max-h-[240px] overflow-y-auto py-1">
              {menus.map((menu) => (
                <div 
                  key={menu.id}
                  className="relative group hover:bg-slate-50"
                >
                  <button
                    onClick={() => {
                      onSelect(menu.id);
                      setIsOpen(false);
                      setIsRenaming(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors ${
                      menu.id === activeMenuId ? 'text-indigo-700 font-medium bg-indigo-50/50' : 'text-slate-600'
                    }`}
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      <SafeIcon 
                        icon={FiIcons.FiGrid} 
                        className={`text-xs flex-shrink-0 ${menu.id === activeMenuId ? 'text-indigo-500' : 'text-slate-400'}`} 
                      />
                      <span className="truncate">{menu.title}</span>
                    </div>
                    {menu.id === activeMenuId && (
                      <SafeIcon icon={FiIcons.FiCheck} className="text-indigo-600 flex-shrink-0" />
                    )}
                  </button>

                  {/* Actions - Visible on Hover for ALL items (if not readOnly) */}
                  {!readOnly && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white shadow-sm rounded-md border border-slate-100 px-1 py-0.5">
                      {menu.id === activeMenuId && (
                        <button 
                          onClick={startRenaming}
                          className="p-1.5 hover:bg-indigo-50 rounded text-slate-400 hover:text-indigo-600 transition-colors"
                          title="Rename"
                        >
                          <SafeIcon icon={FiIcons.FiEdit2} className="text-xs" />
                        </button>
                      )}
                      
                      {menus.length > 1 && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            if(confirm(`Delete "${menu.title}"?`)) {
                              onDelete(menu.id);
                            }
                          }}
                          className="p-1.5 hover:bg-red-50 rounded text-slate-400 hover:text-red-600 transition-colors"
                          title="Delete"
                        >
                          <SafeIcon icon={FiIcons.FiTrash2} className="text-xs" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Add Menu Section */}
            {!readOnly && (
              <div className="p-2 border-t border-slate-100 bg-slate-50">
                {isRenaming ? (
                  <form onSubmit={handleRenameSubmit} className="flex gap-2">
                    <input
                      autoFocus
                      type="text"
                      value={tempName}
                      onChange={(e) => setTempName(e.target.value)}
                      className="flex-1 px-2 py-1 text-xs border border-indigo-200 rounded focus:outline-none focus:border-indigo-500"
                      placeholder="Sitemap Name"
                    />
                    <button type="submit" className="px-2 py-1 bg-indigo-600 text-white rounded text-xs">Save</button>
                    <button type="button" onClick={() => setIsRenaming(false)} className="px-2 py-1 bg-slate-200 text-slate-600 rounded text-xs">X</button>
                  </form>
                ) : (
                  <button 
                    onClick={() => {
                      onAdd();
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white border border-dashed border-slate-300 rounded-lg text-slate-500 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50 transition-all text-xs font-medium"
                  >
                    <SafeIcon icon={FiIcons.FiPlus} />
                    Add New Sitemap
                  </button>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MenuSelector;